/**
 * "Download" for an uploaded video.
 *
 * Asks the API for a short-lived signed link with an attachment Content-Disposition, then
 * hands it to the browser. The browser's downloads UI shows progress and lets the coach
 * cancel, and the file never passes through page memory.
 *
 * A clip has no file of its own (it is a window on its source), so for a clip the button
 * says it downloads the full video. Nothing is cut or re-encoded.
 */

import { useId, useState } from 'react';
import { Button } from './Button';
import { getVideoDownloadUrl } from '../services/prospect-media';
import { getValidatedGcsUrl } from '../utils/url-validation';
import { startUrlDownload } from '../utils/file-download';
import './VideoDownloadButton.css';

interface VideoDownloadButtonProps {
  prospectId: string;
  videoId: string;
  /** A clip downloads its full source video, and the button says so. */
  isClip: boolean;
  /**
   * Shows the "full video, not just this clip" line under the button. Off where there is no
   * room for it (the player's control strip); the button's own label still says "full video".
   */
  showClipHint?: boolean;
  className?: string;
}

export const DOWNLOAD_ERROR_MESSAGE = 'Could not start the download. Please try again.';
export const CLIP_DOWNLOAD_HINT = 'Downloads the full video, not just this clip.';

export const VideoDownloadButton = ({
  prospectId,
  videoId,
  isClip,
  showClipHint = true,
  className,
}: VideoDownloadButtonProps) => {
  // Callers key this component by video id, so a request and its error always belong to the
  // video on screen; nothing here has to reset itself when the video changes.
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hintId = useId();

  const handleClick = async () => {
    if (preparing) return;
    setPreparing(true);
    setError(null);

    try {
      const response = await getVideoDownloadUrl(prospectId, videoId);
      const url = getValidatedGcsUrl(response.downloadUrl);
      if (!url) {
        throw new Error('Download link was not a storage URL');
      }
      startUrlDownload(url, response.fileName);
    } catch (err) {
      console.error('[VideoDownloadButton] Failed to start download', {
        error: err,
        prospectId,
        videoId,
      });
      setError(DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setPreparing(false);
    }
  };

  return (
    <span className={['video-download', className].filter(Boolean).join(' ')}>
      <Button
        variant="secondary"
        onClick={handleClick}
        // aria-disabled rather than disabled: a disabled button drops keyboard focus to the
        // page, and the coach would lose their place in the player. The guard above blocks a
        // second press.
        aria-disabled={preparing}
        aria-describedby={isClip && showClipHint ? hintId : undefined}
      >
        {preparing ? 'Preparing…' : isClip ? 'Download full video' : 'Download'}
      </Button>
      {isClip && showClipHint && (
        <span id={hintId} className="video-download__hint">
          {CLIP_DOWNLOAD_HINT}
        </span>
      )}
      {error && (
        <span className="video-download__error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
};
